import React, { useState, useRef, useEffect } from 'react';
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath } from '@xyflow/react';
import { Delete16Regular } from '@fluentui/react-icons';
import './CSS/CustomEdge.css';

/**
 * Custom Edge avec label éditable pour les DTMF
 */
export default function EditableEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  label,
  data,
}) {
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(label || '');
  const [savedValue, setSavedValue] = useState(null); // Pour tracker la valeur qu'on a essayé de sauvegarder
  const inputRef = useRef(null);

  // Synchroniser editValue avec le label qui vient des props (seulement si on n'édite pas)
  useEffect(() => {
    if (!isEditing) {
      setEditValue(label || '');
    }
  }, [label, isEditing]);

  // Fermer l'édition quand le label est mis à jour avec la valeur sauvegardée
  useEffect(() => {
    if (isEditing && savedValue && label === savedValue) {
      setIsEditing(false);
      setSavedValue(null);
    }
  }, [label, savedValue, isEditing]);

  // Focus l'input quand on passe en mode édition
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Si le label est vide et qu'on a needsInput, ouvrir l'input automatiquement
  useEffect(() => {
    if (data?.needsInput && !label) {
      setIsEditing(true);
    }
  }, [data?.needsInput, label]);

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    // Seulement éditable si c'est un IVR edge (a un callback onLabelChange)
    if (data?.onLabelChange) {
      setIsEditing(true);
      setEditValue(label || '');
    }
  };

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    
    
    // Si vide, ne pas fermer l'input
    if (!trimmedValue) {
      return; // Garder l'input ouvert
    }
    
    // Si la valeur n'a pas changé, juste fermer l'input
    if (trimmedValue === label) {
      setIsEditing(false);
      return;
    }
    
    // Sauvegarder la nouvelle valeur
    if (data?.onLabelChange) {
      setSavedValue(trimmedValue); // Tracker la valeur qu'on essaie de sauvegarder
      data.onLabelChange(id, trimmedValue);
      // Ne pas fermer immédiatement - le useEffect fermera quand label === savedValue
    } else {
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(label || '');
    }
  };

  const handleBlur = () => {
    handleSave();
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (data?.onDelete) {
      data.onDelete(id);
    }
  };

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <div
          className="custom-edge-label-wrapper"
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
          }}
        >
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              className="editable-edge-input"
              placeholder={data?.inputPlaceholder || 'DTMF'}
              maxLength={10}
            />
          ) : (
            <>
              {/* Label de l'edge - double-cliquez pour éditer */}
              {label && (
                <div
                  className="custom-edge-label"
                  onDoubleClick={handleDoubleClick}
                  style={{
                    cursor: data?.onLabelChange ? 'pointer' : 'default',
                  }}
                  title={data?.onLabelChange ? 'Double-cliquez pour éditer le DTMF' : ''}
                >
                  {label}
                </div>
              )}
              {/* Bouton de suppression (icône corbeille mauve) */}
              {data?.onDelete && (
                <button
                  className="custom-edge-delete-btn"
                  onClick={handleDelete}
                  title="Supprimer la liaison"
                >
                  <Delete16Regular />
                </button>
              )}
            </>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
